import { ReactNode } from 'react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { ApolloClient } from '@apollo/client';
import { LocalUserState } from '../types/LocalUserState';
import { useSetupLaunchDarkly } from '../hooks/useSetupLaunchDarkly';
import { LD_CLIENT_ID } from '../constants/launchDarkly';

interface LaunchDarklyProviderProps {
  user: LocalUserState;
  client: ApolloClient;
  children: ReactNode;
}

/**
 * Wraps children with a stable `LDProvider` wrapper at all times, preventing
 * the subtree from unmounting/remounting when the secure-mode HMAC hash becomes
 * available. `deferInitialization` is used so the LD client only initialises
 * once both the username and hash are ready, avoiding anonymous-context 400
 * errors while still keeping the React tree stable.
 *
 * When `LD_CLIENT_ID` is not configured the provider is skipped entirely so
 * there is no overhead in environments where LD is disabled.
 */
export const LaunchDarklyProvider = ({ user, client, children }: LaunchDarklyProviderProps) => {
  const ldEnabled = (LD_CLIENT_ID ?? '').trim().length > 0;
  const { ldHash } = useSetupLaunchDarkly(user, client, ldEnabled);
  const username = (typeof user?.token?.cb?.un === 'string' ? user.token.cb.un.trim() : '') || undefined;

  if (!ldEnabled) {
    return <>{children}</>;
  }

  const ldReady = !!(username && ldHash);

  return (
    <LDProvider
      clientSideID={LD_CLIENT_ID}
      // context is only provided once both username and hash are available;
      // deferInitialization tells LDProvider to wait until context is set
      // before initialising the LD client.
      context={
        ldReady
          ? {
              kind: 'user',
              key: `user-${username}`,
            }
          : undefined
      }
      deferInitialization
      options={{
        allAttributesPrivate: true,
        useReport: true,
        hash: ldHash,
      }}
      reactOptions={{
        useCamelCaseFlagKeys: false,
      }}
    >
      {children}
    </LDProvider>
  );
};
